import type {
  Firestore,
  QueryConstraint,
  Timestamp,
  DocumentData,
} from 'firebase/firestore';
import { BatchOperation } from './types';
const firestoreHandler = async <T>(
  operation: () => Promise<T>,
  message: string,
): Promise<T | null> => {
  try {
    return await operation();
  } catch (error) {
    console.error(`Firestore Error - ${message}:`, error);
    return null; // You can change this to throw an error if needed
  }
};

export const getDocument = async <T>(
  firestore: Firestore,
  collectionPath: string,
  id: string,
): Promise<T | null> => {
  const { getDoc, doc } = await import('firebase/firestore');

  return firestoreHandler(async () => {
    const docSnap = await getDoc(doc(firestore, collectionPath, id));
    return docSnap.exists() ? ({ ...docSnap.data(), id } as T) : null;
  }, `getDocument(${collectionPath}, ${id})`);
};

export const getAllDocuments = async <T>(
  firestore: Firestore,
  collectionPath: string,
): Promise<T[]> => {
  const { getDocs, collection } = await import('firebase/firestore');

  const result = await firestoreHandler(async () => {
    const snapshot = await getDocs(collection(firestore, collectionPath));
    return snapshot.docs.map((doc) => ({ ...(doc.data() as T), id: doc.id }));
  }, `getAllDocuments(${collectionPath})`);
  return result ?? [];
};

export const queryDocuments = async <T>(
  firestore: Firestore,
  collectionPath: string,
  ...constraints: QueryConstraint[]
): Promise<{ documents: T[]; total: number }> => {
  const { getDocs, query, collection } = await import('firebase/firestore');

  const result = await firestoreHandler(async () => {
    const q = query(collection(firestore, collectionPath), ...constraints);
    const snapshot = await getDocs(q);
    const documents = snapshot.docs.map((doc) => ({
      ...(doc.data() as T),
      id: doc.id,
    }));
    const total = snapshot.size;
    return { documents, total };
  }, `queryDocuments(${collectionPath})`);

  return result ?? { documents: [], total: 0 };
};

export const addDocument = async <T>(
  firestore: Firestore,
  collectionPath: string,
  data: Omit<T, 'id'>,
): Promise<string | null> => {
  const { addDoc, collection } = await import('firebase/firestore');

  return firestoreHandler(async () => {
    const docRef = await addDoc(collection(firestore, collectionPath), data);
    return docRef.id;
  }, `addDocument(${collectionPath})`);
};

export const setDocument = async <T extends DocumentData>(
  firestore: Firestore,
  collectionPath: string,
  id: string,
  data: T,
): Promise<void> => {
  const { setDoc, doc } = await import('firebase/firestore');

  return firestoreHandler(async () => {
    const docId =
      id === '' ? await generateDocId(firestore, collectionPath) : id;
    await setDoc(doc(firestore, collectionPath, docId), data);
  }, `setDocument(${collectionPath}, ${id})`) as Promise<void>;
};

export const updateDocument = async <T>(
  firestore: Firestore,
  collectionPath: string,
  id: string,
  data: Partial<T>,
): Promise<void> => {
  const { updateDoc, doc } = await import('firebase/firestore');

  return firestoreHandler(async () => {
    await updateDoc(doc(firestore, collectionPath, id), data);
  }, `updateDocument(${collectionPath}, ${id})`) as Promise<void>;
};

export const deleteDocument = async (
  firestore: Firestore,
  collectionPath: string,
  id: string,
): Promise<void> => {
  const { deleteDoc, doc } = await import('firebase/firestore');

  return firestoreHandler(async () => {
    await deleteDoc(doc(firestore, collectionPath, id));
  }, `deleteDocument(${collectionPath}, ${id})`) as Promise<void>;
};

export const generateDocId = async (firestore: Firestore, ref: string) => {
  const { doc, collection } = await import('firebase/firestore');
  const newDocRef = await doc(collection(firestore, ref));
  return newDocRef.id;
};

export const generateTimestamp = async (): Promise<Timestamp> => {
  const { serverTimestamp } = await import('firebase/firestore');
  return serverTimestamp() as Timestamp;
};

export const batchWrite = async (
  firestore: Firestore,
  operations: BatchOperation[],
) => {
  const { writeBatch, doc, collection } = await import('firebase/firestore');

  return firestoreHandler(async () => {
    const batch = writeBatch(firestore);
    operations.forEach(async (op) => {
      const fbCollection = collection(firestore, op.collectionPath);
      const ref = doc(fbCollection, op.id);
      if (op.type === 'set' && op.data) {
        const docId =
          op.id === ''
            ? await generateDocId(firestore, op.collectionPath)
            : op.id;
        batch.set(ref, { ...op.data, id: docId });
      } else if (op.type === 'update' && op.data) batch.update(ref, op.data);
      else if (op.type === 'delete') batch.delete(ref);
    });

    await batch.commit();
  }, `batchWrite(${operations.length} operations)`);
};

// export const listenToFireStoreCollection = async <T>(
//   firestore: Firestore,
//   collectionPath: string,
//   callback: (documents: T[]) => void,
//   ...constraints: QueryConstraint[]
// ): Promise<() => void> => {
//   const { onSnapshot, query, collection } = await import('firebase/firestore');
//   const q = query(collection(firestore, collectionPath), ...constraints);
//   return onSnapshot(
//     q,
//     (snapshot) => {
//       const documents = snapshot.docs.map((doc) => ({
//         ...(doc.data() as T),
//         id: doc.id,
//       }));
//       callback(documents);
//     },
//     (error) => {
//       console.error(`Error listening to collection ${collectionPath}:`, error);
//     }
//   );
// };

export const listenToFirestoreDocument = async <T>(
  firestore: Firestore,
  collectionPath: string,
  id: string,
  callback: (document: T | null) => void,
): Promise<() => void> => {
  const { onSnapshot, doc } = await import('firebase/firestore');
  return onSnapshot(
    doc(firestore, collectionPath, id),
    (docSnap) => {
      if (docSnap.exists()) {
        const document = { ...docSnap.data(), id } as T;
        callback(document);
      } else {
        console.warn(`Document ${id} does not exist in ${collectionPath}`);
        callback(null);
      }
    },
    (error) => {
      console.error(
        `Error listening to document ${collectionPath}/${id}:`,
        error,
      );
      callback(null);
    },
  );
};

export const functionsCreateCheckoutSession = async (
  quantity: number,
  orgId: string,
) => {
  const { getFunctionsService } = await import('./firebaseConfig');
  const functions = await getFunctionsService();
  const { httpsCallable } = await import('firebase/functions');
  try {
    const createCheckoutSession = httpsCallable(
      functions,
      'createCheckoutSession',
    );
    const result = await createCheckoutSession({ quantity, orgId });

    const data = result.data as { url?: string };
    if (data.url) {
      window.location.href = data.url;
    } else {
      console.error('No URL returned from createCheckoutSession function');
    }
  } catch (error) {
    console.error('Error calling createCheckoutSession function:', error);
  }
};

export const functionsCreateCustomerPortalSession = async (
  customerId: string,
) => {
  const { getFunctionsService } = await import('./firebaseConfig');
  const functions = await getFunctionsService();
  const { httpsCallable } = await import('firebase/functions');
  try {
    const createBillingPortalSession = httpsCallable(
      functions,
      'createBillingPortalSession',
    );
    const result = await createBillingPortalSession({ customerId });

    const data = result.data as { url?: string };
    if (data.url) {
      window.location.href = data.url;
    } else {
      console.error(
        'No URL returned from createCustomerPortalSession function',
      );
    }
  } catch (error) {
    console.error('Error calling createCustomerPortalSession function:', error);
  }
};

export const functionsSendWelcomeEmail = async (to: string, name: string) => {
  const { getFunctionsService } = await import('./firebaseConfig');
  const functions = await getFunctionsService();
  const { httpsCallable } = await import('firebase/functions');
  try {
    const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
    await sendWelcomeEmail({ to, name });
  } catch (error) {
    console.error('Error calling sendWelcomeEmail function:', error);
  }
};

export const functionsSendInviteEmail = async (
  to: string,
  invitedBy: string,
  organization: string,
) => {
  const { getFunctionsService } = await import('./firebaseConfig');
  const functions = await getFunctionsService();
  const { httpsCallable } = await import('firebase/functions');
  try {
    const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
    await sendInviteEmail({
      to,
      invitedBy,
      organization,
      inviteUrl: `${window.location.origin}/auth/signup`,
    });
  } catch (error) {
    console.error('Error calling sendInviteEmail function:', error);
  }
};
